import { ITranscription, ITranscriptions } from '@/types/task.interface'

const getSegmentKey = (segment: {
	start: number
	end: number
	content: string
}) => `${segment.start}-${segment.end}-${segment.content}`

const getChunkKey = (chunk: ITranscription) =>
	`${chunk.id}-${chunk.start}-${chunk.end}`

const hasChunkText = (chunk: ITranscription) => {
	return Boolean(
		chunk.content.trim() ||
			chunk.segments.some(segment => segment.content.trim())
	)
}

const filterEmptyChunks = (chunks: ITranscription[]) => {
	return chunks.filter(hasChunkText)
}

export const normalizeTranscriptions = (
	data: ITranscriptions
): ITranscriptions => {
	return {
		...data,
		transcriptions: {
			original: filterEmptyChunks(data.transcriptions.original),
			translation: filterEmptyChunks(data.transcriptions.translation)
		}
	}
}

const mergeTranscriptionChunks = (
	prevChunks: ITranscription[],
	nextChunks: ITranscription[]
) => {
	const chunksMap = new Map<string, ITranscription>()

	prevChunks.forEach(chunk => {
		chunksMap.set(getChunkKey(chunk), chunk)
	})

	nextChunks.forEach(chunk => {
		if (!hasChunkText(chunk)) return

		const key = getChunkKey(chunk)
		const existingChunk = chunksMap.get(key)

		if (!existingChunk) {
			chunksMap.set(key, chunk)
			return
		}

		const seenSegments = new Set(
			existingChunk.segments.map(segment => getSegmentKey(segment))
		)

		const newSegments = chunk.segments.filter(segment => {
			const segmentKey = getSegmentKey(segment)

			if (seenSegments.has(segmentKey)) return false

			seenSegments.add(segmentKey)
			return true
		})

		chunksMap.set(key, {
			...existingChunk,
			...chunk,
			content: chunk.content.trim() ? chunk.content : existingChunk.content,
			segments: [...existingChunk.segments, ...newSegments].sort(
				(a, b) => a.start - b.start
			)
		})
	})

	return Array.from(chunksMap.values()).sort((a, b) => a.start - b.start)
}

export const mergeTranscriptions = (
	prev: ITranscriptions,
	next: ITranscriptions
): ITranscriptions => {
	return {
		...next,
		transcriptions: {
			original: mergeTranscriptionChunks(
				prev.transcriptions.original,
				next.transcriptions.original
			),
			translation: mergeTranscriptionChunks(
				prev.transcriptions.translation,
				next.transcriptions.translation
			)
		}
	}
}

const getLastWordEnd = (chunks: ITranscription[]) => {
	for (let chunkIndex = chunks.length - 1; chunkIndex >= 0; chunkIndex--) {
		const segments = chunks[chunkIndex].segments

		for (
			let segmentIndex = segments.length - 1;
			segmentIndex >= 0;
			segmentIndex--
		) {
			const end = segments[segmentIndex]?.end

			if (typeof end === 'number') return end
		}
	}

	return null
}

export const getNextStartAfter = (data: ITranscriptions) => {
	const originalEnd = getLastWordEnd(data.transcriptions.original)
	const translationEnd = getLastWordEnd(data.transcriptions.translation)

	if (typeof originalEnd !== 'number' || typeof translationEnd !== 'number') {
		return null
	}

	return Math.min(originalEnd, translationEnd)
}
